import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, PauseCircle, PlayCircle } from 'lucide-react';

import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  automationPauseService,
  type AutomationPauseState,
} from '@/services/automationPauseService';
import { supabase } from '@/lib/supabaseClient';

interface AutomationPauseToggleProps {
  tenantId: string;
}

type PendingAction = 'pause' | 'resume' | null;

export function AutomationPauseToggle({ tenantId }: AutomationPauseToggleProps) {
  const [state, setState] = useState<AutomationPauseState | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const [{ data: adminCheck }, result] = await Promise.all([
          supabase.rpc('is_admin_or_superadmin'),
          automationPauseService.getState(tenantId),
        ]);
        if (cancelled) return;
        setIsAdmin(!!adminCheck);
        setState(result);
      } catch (error) {
        console.error(error);
        if (!cancelled) toast.error('Failed to load pause state');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    // Realtime updates so all admins see toggle changes live
    const channel = supabase
      .channel(`tenant-automations-paused-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenants',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          const next = payload.new as {
            automations_paused?: boolean;
            automations_paused_at?: string | null;
            automations_paused_by?: string | null;
          };
          setState({
            paused: !!next.automations_paused,
            pausedAt: next.automations_paused_at ?? null,
            pausedBy: next.automations_paused_by ?? null,
          });
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [tenantId]);

  const requestToggle = (nextChecked: boolean) => {
    setPending(nextChecked ? 'pause' : 'resume');
  };

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      setSubmitting(true);
      const next =
        pending === 'pause'
          ? await automationPauseService.pause(tenantId)
          : await automationPauseService.unpause(tenantId);
      setState(next);
      toast.success(
        pending === 'pause' ? 'Automations paused' : 'Automations resumed'
      );
    } catch (error) {
      console.error(error);
      toast.error(
        pending === 'pause'
          ? 'Failed to pause automations'
          : 'Failed to resume automations'
      );
    } finally {
      setSubmitting(false);
      setPending(null);
    }
  };

  const paused = !!state?.paused;
  const Icon = paused ? PauseCircle : PlayCircle;

  // Non-admins don't see the toggle at all. Permission errors from the RPC
  // (e.g. someone calling it directly) are surfaced as toast errors above.
  if (isAdmin === false) {
    return null;
  }

  return (
    <>
      <div
        className={
          paused
            ? 'flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3'
            : 'flex items-center justify-between gap-4 rounded-lg border bg-muted/30 px-4 py-3'
        }
      >
        <div className="flex items-start gap-3">
          <Icon
            className={
              paused
                ? 'mt-0.5 h-5 w-5 shrink-0 text-destructive'
                : 'mt-0.5 h-5 w-5 shrink-0 text-muted-foreground'
            }
          />
          <div>
            <div className="text-sm font-medium">
              {paused
                ? 'Automations are paused'
                : 'Automations are active'}
            </div>
            <div className="text-xs text-muted-foreground">
              {paused
                ? 'No triggers will fire. Each automation’s on/off state is preserved and will be restored when you resume.'
                : 'Pause to stop all triggers without changing individual on/off states.'}
            </div>
          </div>
        </div>
        <Switch
          checked={paused}
          disabled={loading || submitting}
          onCheckedChange={requestToggle}
          aria-label={paused ? 'Resume automations' : 'Pause automations'}
        />
      </div>

      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !submitting) setPending(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {pending === 'pause'
                ? 'Pause all automations?'
                : 'Resume automations?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {pending === 'pause' ? (
                  <>
                    <p>
                      This pauses every automation across this tenant. Before
                      you confirm, note the following behavior:
                    </p>
                    <ul className="list-disc space-y-1.5 pl-5">
                      <li>
                        <span className="font-medium">
                          Individual on/off states are preserved.
                        </span>{' '}
                        When you resume, automations that were on stay on, and
                        automations that were off stay off.
                      </li>
                      <li>
                        <span className="font-medium">
                          Events during the pause are dropped, not queued.
                        </span>{' '}
                        A stage change or field update that happens while
                        paused will not fire its automation later.
                      </li>
                      <li>
                        <span className="font-medium">
                          In-flight executions finish.
                        </span>{' '}
                        Pausing only stops new triggers; anything already
                        running completes.
                      </li>
                      <li>
                        <span className="font-medium">
                          SLA timers keep counting.
                        </span>{' '}
                        When you resume, SLA checks run against real elapsed
                        time — they don’t reset.
                      </li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p>
                      This re-enables the automation engine using each
                      automation’s previous on/off state:
                    </p>
                    <ul className="list-disc space-y-1.5 pl-5">
                      <li>
                        Automations that were on before the pause will fire
                        again on new events.
                      </li>
                      <li>
                        Automations that were off before the pause will stay
                        off.
                      </li>
                      <li>
                        Events that occurred during the pause will not be
                        replayed.
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={submitting}
              className={
                pending === 'pause'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
            >
              {submitting
                ? 'Working…'
                : pending === 'pause'
                ? 'Pause automations'
                : 'Resume automations'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
